import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button, MetaPill, toast } from "@flint/ui";
import { paiseToInrExact } from "../../../lib/format.ts";
import { priceChainPaise } from "../price-math.ts";
import { PricingEntryDialog } from "./pricing-entry-dialog.tsx";
import { swatchColor } from "./bundle-builder.tsx";
import {
  MAX_PACKS,
  type PricingEntry,
  compositionPairs,
  entryPiecesOnHand,
  usableEntries,
} from "./size-ratio.ts";

export interface PricingEntryListProps {
  value: PricingEntry[];
  onChange: (next: PricingEntry[]) => void;
  /** Raw GST field value, in percent — for the incl-GST figure on each card. */
  taxPct: string;
  /** The product's variant axes — feeds the dialog's Bundle Builder (#235). */
  variantAttributes?: readonly { name: string; values: string[] }[] | undefined;
  /** The generated variant rows — the builder's steppers compose over these. */
  productVariants?:
    | readonly { attributeValues: Record<string, string>; sku: string }[]
    | undefined;
  /**
   * Coverage rule at bundle-save time (ADR-0065 companion): given the entry
   * being saved (its pieces on hand) and which index it replaces, returns the
   * refusal message when the bundles would exceed the opening stock — or null.
   * Absent on the edit form, which has no opening stock.
   */
  coverageGuard?: ((candidatePiecesOnHand: number, editIndex?: number) => string | null) | undefined;
}

function pctToBps(pct: string): number {
  const n = Number.parseFloat(pct);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

/**
 * Every bundle a product is sold in — a card per entry, an **Add bundle**
 * button, and the dialog that fills them (spec 13, R4; a bundle **is** a
 * pricing entry, spec 48 R1). Lives in the "Bundle Builder" card (#265).
 *
 * A style is priced per library pack, and each pack carries its own price,
 * discount and minimum. That is why this is a list rather than a set of fields:
 * there is no single price to put in a single box.
 *
 * **Entry [0] is the primary.** `sold_by`, `size_ratio`, `pieces_per_pack`,
 * `moq`, `discount_bps` and `items.default_rate_paise` are all mirrored from it
 * server-side, so a reader that knows nothing about entries still sees a
 * coherent listing.
 *
 * Compositions are read-only here — they live in the pack library, not on the
 * entry (R4). Stock on hand is the Inventory section, per warehouse.
 */
export function PricingEntryList({
  value,
  onChange,
  taxPct,
  variantAttributes,
  productVariants,
  coverageGuard,
}: PricingEntryListProps) {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  // `value` is what the form holds and what the indices below address; `priced`
  // is the subset that carries a price, and is only ever used for the money
  // summaries. They used to be the same variable, which meant every edit rebuilt
  // the array from the *filtered* list — writing back a shorter array than the
  // one the indices came from, so a priceless entry was quietly deleted by the
  // act of editing a different row. Keeping the two apart is what makes an
  // index here mean the same thing to `onChange`.
  const priced = usableEntries(value);
  const atCap = value.length >= MAX_PACKS;
  const taxBps = pctToBps(taxPct);
  // Summed per entry, incl-GST, so the total equals the sum of the figures printed on
  // the cards above it — the only check an operator can do by eye. Taxing the ex-GST
  // total once can differ from that by a paisa.
  const lot = priced.reduce(
    (sum, e) =>
      sum + priceChainPaise(BigInt(e.pricePaise || "0"), e.discountBps || 0, taxBps).finalPaise,
    0n,
  );

  // Confirmed on the spot. The entry list is well below the fold on a long
  // form, so a card appearing off-screen is not by itself feedback that the
  // dialog did anything. Nothing is persisted until the product is saved, and
  // the copy says so rather than implying a write.
  const upsert = (entry: PricingEntry, index: number | null) => {
    onChange(index === null ? [...value, entry] : value.map((e, i) => (i === index ? entry : e)));
    toast.success(index === null ? `${entry.label} added` : `${entry.label} updated`, {
      description: "Saved with the product.",
    });
  };

  const remove = (index: number) => {
    const removed = value[index];
    onChange(value.filter((_, i) => i !== index));
    if (removed) toast.success(`${removed.label} removed`);
  };

  return (
    <div className="flex flex-col gap-2.5">
      {/* No sub-header here — the "Bundle Builder" card heading directly above
          already names the list (#265); a second label was the old
          "Pricing"/"Pricing & pack" mix the UX review called out. */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          intent="primary"
          size="sm"
          disabled={atCap}
          onClick={() => setAdding(true)}
          type="button"
        >
          <Plus className="size-3.5" aria-hidden />
          Add bundle
        </Button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
          <p className="text-sm text-ink-2">No bundles yet.</p>
          <p className="mt-1 text-xs text-muted">
            Build a bundle — publishing needs at least one.
          </p>
        </div>
      ) : (
        // Every entry on the record, not just the priced ones: a card the
        // operator can't see is a card they can't fix or remove, and the index
        // it edits under has to be the index the form holds.
        <ul className="flex flex-col gap-2">
          {value.map((e, i) => {
            // was → discount → final, all incl-GST, from the one helper the grid and
            // the detail page also use (spec 13 R6a).
            const { wasPaise, finalPaise, savingPaise } = priceChainPaise(
              BigInt(e.pricePaise || "0"),
              e.discountBps || 0,
              taxBps,
            );
            return (
              <li
                key={`${e.kind}-${e.label}-${i}`}
                className="flex items-start justify-between gap-3 rounded-sm border border-line bg-paper px-3.5 py-3"
              >
                {/* `flex-1 min-w-0` so a wide composition row (a variant-composed
                    bundle can carry a dozen pills) wraps INSIDE this column
                    rather than pushing the price and actions onto their own
                    line, where `flex-wrap` used to drop them back to the left
                    edge. The actions belong at the card's right edge at every
                    width. */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium text-ink">{e.label}</span>
                    {/* The primary is the first *priced* entry, because that is
                        the one the submit path sends as `pricingEntries[0]` and
                        the server mirrors into the listing columns — not simply
                        the top card. */}
                    {e === priced[0] ? (
                      <span className="rounded-full bg-raised px-2 py-0.5 text-xs text-muted">
                        primary
                      </span>
                    ) : null}
                    <span className="font-mono text-xs text-muted tabular-nums">
                      {e.pieces} pcs
                    </span>
                    {/* Quantity only when it's set — an absent one renders
                        nothing rather than a `—` on a line that already reads
                        as a list of figures.

                        Tested against `undefined`, not truthiness: since
                        ADR-0056 fulfilment can deduct an entry to 0, and a
                        falsy test would hide exactly the sold-out case —
                        making "none left" look identical to "never
                        tracked". */}
                    {e.packsOnHand !== undefined ? (
                      <span className={e.packsOnHand === 0 ? "text-xs text-bad" : "text-xs text-muted"}>
                        ·{" "}
                        <span className="font-mono tabular-nums">{e.packsOnHand}</span> packs ·{" "}
                        <span className="font-mono tabular-nums">
                          {entryPiecesOnHand(e).toLocaleString("en-IN")}
                        </span>{" "}
                        pcs
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {/* A variant-composed bundle shows its per-variant breakdown
                        ("S · Black ×2") — the size pills would be the same data
                        with the colours summed away. Size pills remain for
                        library packs and size-composed bundles. */}
                    {e.variantComposition && e.variantComposition.length > 0
                      ? e.variantComposition.map((vc) => (
                          <MetaPill key={Object.values(vc.attributeValues).join("·")}>
                            {Object.values(vc.attributeValues).join(" · ")}×{vc.qty}
                          </MetaPill>
                        ))
                      : compositionPairs(e.sizeRatio).map((c) => (
                          <MetaPill key={c.label}>
                            {c.label}×{c.qty}
                          </MetaPill>
                        ))}
                    {/* The bundle's colour overlay (#235): one swatch + name for
                        Single, a count for Mixed. Absent on pre-#235 entries. */}
                    {e.colorSpec ? (
                      <span className="ml-1 inline-flex items-center gap-1 text-xs text-muted">
                        {e.colorSpec.mode === "single" && e.colorSpec.colors[0] ? (
                          <>
                            <ColorDot color={e.colorSpec.colors[0]} />
                            {e.colorSpec.colors[0]}
                          </>
                        ) : (
                          <>
                            {e.colorSpec.colors.slice(0, 4).map((c) => (
                              <ColorDot key={c} color={c} />
                            ))}
                            Mixed · {e.colorSpec.colors.length}{" "}
                            {e.colorSpec.colors.length === 1 ? "color" : "colors"}
                          </>
                        )}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    MOQ <span className="font-mono tabular-nums">{e.moq ?? "—"}</span>
                    {e.moq ? " packs" : ""}
                  </p>
                </div>

                <div className="flex shrink-0 items-start gap-3">
                  {/* was → discount → final, the same three figures in the same order
                      as the detail page and the grid row for this product. */}
                  <div className="text-right">
                    {e.discountBps > 0 ? (
                      <div className="text-xs text-muted">
                        <span className="font-mono tabular-nums line-through">
                          {paiseToInrExact(wasPaise.toString())}
                        </span>{" "}
                        <span className="font-mono tabular-nums">−{e.discountBps / 100}%</span> ·{" "}
                        <span className="font-mono tabular-nums">
                          −{paiseToInrExact(savingPaise.toString())}
                        </span>
                      </div>
                    ) : null}
                    <div className="font-mono font-semibold tabular-nums text-ink">
                      {paiseToInrExact(finalPaise.toString())}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <IconButton label={`Edit ${e.label}`} onClick={() => setEditIndex(i)}>
                      <Pencil className="size-4" aria-hidden />
                    </IconButton>
                    <IconButton label={`Remove ${e.label}`} danger onClick={() => remove(i)}>
                      <Trash2 className="size-4" aria-hidden />
                    </IconButton>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Money, so priced entries only — a ₹0 row adds nothing to "one of
          each", and the primary the catalog quotes is the first *priced* entry,
          which is what the submit path sends as `pricingEntries[0]`. */}
      {priced.length > 1 ? (
        <p className="text-xs text-muted">
          {priced.length} bundles ·{" "}
          <span className="font-mono font-semibold tabular-nums text-ink">
            {paiseToInrExact(lot.toString())}
          </span>{" "}
          for one of each. {priced[0]?.label} is what the catalog quotes.
        </p>
      ) : null}

      {atCap ? (
        <p className="text-xs text-muted">{MAX_PACKS} bundles is the maximum for one product.</p>
      ) : null}

      {/* Mounted only while open, so each open seeds fresh from the entry being
          edited (if any). Keeping it mounted meant carrying the previous open's
          state and re-seeding through an effect, which showed a stale entry
          after a save. */}
      {adding ? (
        <PricingEntryDialog
          open
          onOpenChange={setAdding}
          // Every entry, priced or not: the dialog's "already priced on this
          // product" check is about the pack link, and it skips itself by
          // `editIndex` — which indexes `value`, so this list has to as well.
          siblings={value}
          taxPct={taxPct}
          variantAttributes={variantAttributes}
          productVariants={productVariants}
          coverageGuard={coverageGuard}
          onSave={(entry) => upsert(entry, null)}
        />
      ) : null}
      {editIndex !== null ? (
        <PricingEntryDialog
          open
          onOpenChange={(next) => !next && setEditIndex(null)}
          siblings={value}
          editIndex={editIndex}
          taxPct={taxPct}
          variantAttributes={variantAttributes}
          productVariants={productVariants}
          coverageGuard={coverageGuard}
          onSave={(entry) => upsert(entry, editIndex)}
        />
      ) : null}
    </div>
  );
}

/** A small colour swatch; neutral initial when the name isn't a CSS colour. */
function ColorDot({ color }: { color: string }) {
  const css = swatchColor(color);
  return (
    <span
      title={color}
      className={`inline-block size-3 rounded-full border border-line ${css ? "" : "bg-raised"}`}
      {...(css ? { style: { backgroundColor: css } } : {})}
      aria-hidden
    />
  );
}

function IconButton({
  label,
  danger,
  onClick,
  children,
}: {
  label: string;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`rounded-sm p-1.5 text-muted transition-colors hover:bg-raised focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-100 ${
        danger ? "hover:text-bad" : "hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}
